import React, { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { 
  Shield, 
  Users, 
  Edit, 
  Copy, 
  Trash2,
  Building2,
  CheckCircle,
  XCircle,
  Calendar,
  Activity
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/enterprise`;

const RoleDetailsModal = ({ 
  open, 
  onClose, 
  role,
  onEdit,
  onDuplicate,
  onDelete
}) => {
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && role) {
      fetchAssignedUsers();
    }
  }, [open, role]);

  const fetchAssignedUsers = async () => {
    if (!role) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/roles/${role.id}/users`);
      setAssignedUsers(response.data || []);
    } catch (error) {
      console.error("Error fetching assigned users:", error);
      setAssignedUsers([]);
    } finally {
      setLoading(false);
    }
  };

  if (!role) return null;

  const getRoleScopeLabel = (scope) => {
    const labels = {
      company: "Company",
      subsidiary: "Subsidiary",
      multi_subsidiary: "Multi-Subsidiary",
      department: "Department",
      platform: "Platform",
      custom: "Custom"
    };
    return labels[scope] || scope;
  };

  const getRoleScopeDescription = (scope) => {
    const descriptions = {
      company: "Access to all company-wide resources and data",
      subsidiary: "Access limited to specific subsidiary",
      multi_subsidiary: "Access to multiple subsidiaries",
      department: "Access limited to specific department",
      platform: "Platform-wide administrative access",
      custom: "Custom access scope"
    };
    return descriptions[scope] || "";
  };

  // Group permissions by category
  const groupedPermissions = (role.permissions || []).reduce((acc, perm) => {
    const category = perm.split('_')[0] || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(perm);
    return acc;
  }, {});

  const getCategoryLabel = (category) => {
    const labels = {
      view: "View Permissions",
      create: "Create Permissions",
      edit: "Edit Permissions",
      delete: "Delete Permissions",
      manage: "Management Permissions",
      export: "Export Permissions",
      other: "Other Permissions"
    };
    return labels[category] || category.charAt(0).toUpperCase() + category.slice(1);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Shield className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <div className="text-xl font-bold">{role.name}</div>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant={role.is_system_role ? "secondary" : "default"}>
                    {role.is_system_role ? "System Role" : "Custom Role"}
                  </Badge>
                  <Badge variant="outline">
                    {getRoleScopeLabel(role.role_scope)}
                  </Badge>
                  {role.level && (
                    <Badge variant="outline" className="text-xs">
                      Level {role.level}
                    </Badge>
                  )}
                </div>
              </div>
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Quick Actions */}
          <div className="flex gap-2 pb-4 border-b">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                onEdit && onEdit(role);
                onClose();
              }}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit Role
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                onDuplicate && onDuplicate(role);
                onClose();
              }}
            >
              <Copy className="h-4 w-4 mr-2" />
              Duplicate
            </Button>
            {!role.is_system_role && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  onDelete && onDelete(role);
                  onClose();
                }}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            )}
          </div>

          {/* Role Information */}
          <Card className="bg-gray-50">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Activity className="h-4 w-4" />
                ROLE INFORMATION
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500">Description</label>
                  <p className="text-sm text-gray-900">{role.description || "No description provided"}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Scope</label>
                  <p className="text-sm text-gray-900">{getRoleScopeDescription(role.role_scope)}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500">Hierarchy Level</label>
                    <p className="text-sm font-medium text-gray-900">Level {role.level || "N/A"}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Total Permissions</label>
                    <p className="text-sm font-medium text-gray-900">{role.permissions?.length || 0}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Assigned Users */}
          <Card className="bg-blue-50">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Users className="h-4 w-4" />
                ASSIGNED USERS ({assignedUsers.length})
              </h3>
              {loading ? (
                <p className="text-sm text-gray-500">Loading users...</p>
              ) : assignedUsers.length > 0 ? (
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {assignedUsers.slice(0, 10).map((user) => (
                    <div key={user.id} className="bg-white rounded-lg p-2 flex items-center gap-2">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <Users className="h-4 w-4 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{user.full_name || user.email}</p>
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                      </div>
                    </div>
                  ))}
                  {assignedUsers.length > 10 && (
                    <p className="text-xs text-gray-500 text-center pt-2">
                      +{assignedUsers.length - 10} more users
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No users assigned to this role</p>
              )}
            </CardContent>
          </Card>

          {/* Permissions */}
          <Card>
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Shield className="h-4 w-4" />
                PERMISSIONS ({role.permissions?.length || 0})
              </h3>
              {Object.keys(groupedPermissions).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(groupedPermissions).map(([category, perms]) => (
                    <div key={category}>
                      <h4 className="text-xs font-semibold text-gray-600 mb-2 uppercase">
                        {getCategoryLabel(category)}
                      </h4>
                      <div className="grid grid-cols-2 gap-2">
                        {perms.map((perm, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            <CheckCircle className="h-3 w-3 text-green-600 flex-shrink-0" />
                            <span className="truncate">{perm.replace(/_/g, ' ')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No permissions defined</p>
              )}
            </CardContent>
          </Card>

          {/* Metadata */}
          <Card className="bg-gray-50">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                METADATA
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="text-xs text-gray-500">Role ID</label>
                  <p className="text-xs font-mono text-gray-900">{role.id?.slice(0, 16)}...</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Type</label>
                  <p className="text-gray-900">{role.is_system_role ? "System Role" : "Custom Role"}</p>
                </div>
                {role.created_at && (
                  <div>
                    <label className="text-xs text-gray-500">Created</label>
                    <p className="text-gray-900">{new Date(role.created_at).toLocaleDateString()}</p>
                  </div>
                )}
                {role.updated_at && (
                  <div>
                    <label className="text-xs text-gray-500">Last Modified</label>
                    <p className="text-gray-900">{new Date(role.updated_at).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RoleDetailsModal;
